import DataTable, {type DataTableRef} from 'datatables.net-react'
import 'datatables.net-dt/css/dataTables.dataTables.css';
import DT, {type Config} from 'datatables.net';
import React, {
    forwardRef,
    type Ref,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from "react";
import {formattedDate} from "../../util/ReplaceText.tsx";
import type {Instance, Snapshot} from "../../lib/ModelTypes.ts";
import {swal_util} from "../../lib/SwalUtil.ts";
import {getDatatableLanguagePack} from "./DatatableLanguagePack.tsx";
import type {ApiRowMethods} from "datatables.net-dt";
import type {RowColumnRender} from "./instance/type.ts";
import {useTranslation} from "react-i18next";


export interface SnapshotDataRef {
    getSelectedData: () => Snapshot[];
    redraw: (snapshots: Snapshot[]) => void;
    delete: (snapshot: Snapshot) => void;
    modify: (snapshot: Snapshot) => void;
}

type DataTableProps = {
    ref?: Ref<SnapshotDataRef>;
    onClickDelete(snapshot: Snapshot): void;
    onSelectSnapshot(snapshot: Snapshot): void;
    initialSnapshots?: Snapshot[],
};


const SnapshotTable = forwardRef<SnapshotDataRef, DataTableProps>(
    (props, ref) => {
        const {t, i18n} = useTranslation();
        DataTable.use(DT);

        const [refreshTime, setRefreshTime] = useState(new Date());
        const dataTableRef = useRef<DataTableRef>(null);
        const snapshotsRef = useRef<Snapshot[]>(props.initialSnapshots ?? []);
        const rowsMap = useRef(new Map<number, ApiRowMethods<Instance>>());
        const occurLangChange = useRef(false);

        const checkedIdList = useMemo<Set<number>>(() => new Set<number>(), []);
        const [isLoading, setLoading] = useState(true);

        useImperativeHandle(ref, () => ({
            getSelectedData: () => snapshotsRef.current.filter((row) => checkedIdList.has(row.id)),
            redraw: (snapshots: Snapshot[]) => redraw(snapshots).finally(),
            delete(snapshot: Snapshot) {
                const snapshots = snapshotsRef.current;

                const index = snapshots.findIndex((item) => item.id === snapshot.id);
                if (index < 0) {
                    return;
                }
                snapshots.splice(index, 1);
                invalidate();
            },
            modify(snapshot: Snapshot) {
                const snapshots = snapshotsRef.current;

                const index = snapshots.findIndex((item) => item.id === snapshot.id);
                if (index < 0) {
                    return;
                }
                snapshots[index] = {...snapshot, name: snapshot.name};
                invalidate();
            }
        }));

        async function redraw(snapshots: Snapshot[]) {
            try {
                setLoading(false);
                snapshotsRef.current = snapshots;
                invalidate();
            } catch (err) {
                await swal_util.show_error(`${t("snapshot.errorLoadSnapshot")} ${t("common.adminContact")}`);
                console.error(err);
            }
        }

        function handleCheckboxChange(id: number, checked: boolean) {
            if (checked) {
                checkedIdList.add(id);
            } else {
                checkedIdList.delete(id);
            }
        }

        function handleSelectAll(e: React.UIEvent) {
            const allCheckBox = e.target as HTMLInputElement;

            Array.from(document.getElementsByClassName("check-snapshot")).forEach(element => {
                if (!(element instanceof HTMLInputElement)) {
                    return;
                }
                const idStr = element.dataset["id"];
                if (!idStr) return;
                const id = parseInt(idStr, 10);

                element.checked = allCheckBox.checked;

                handleCheckboxChange(id, element.checked);
            });
        }

        function status_column(status: string) {
            let color = "";

            switch (status) {
                case "ACTIVE":
                    color = '#264EDD';
                    break;
                case "SHUTOFF":
                    color = '#dc3545';
                    break;
                case "HARD_REBOOT":
                    color = '#6610f2';
                    break;
                case "PAUSED":
                    color = '#fd7e14';
                    break;
            }

            return `<span style="color:${color}">${status}</span>`
        }

        const columns = [
            {
                data: 'id',
                orderable: false,
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
                render: (data: number) => {
                    return `<input type="checkbox" class="check-snapshot form-check-input" data-id="${data}" value="${data}" >`;
                },
            },
            {data: 'name', className: "align-center"},
            {data: 'os_image_name'},
            {
                data: 'status', render: (data: string) => {
                    return status_column(data);
                },
            },
            {
                data: 'created', render: (data: string) => {
                    return formattedDate(data);
                }
            },
            {
                data: '',
                render: (_data: string, _type: string, row: Snapshot) => {
                    const btn_modify = `<button class="btn btn-primary modify" data-id="${row.id}" style="font-size: 14px" data-i18n-key="common.edit">${t("common.edit")}</button>`;
                    const btn_delete = `<button class="btn btn-danger delete" style="font-size: 14px" data-i18n-key="common.delete">${t("common.delete")}</button>`;
                    return `${btn_modify} ${btn_delete}`
                },
                orderable: false,
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
            }
        ];
        const columnRendersMap = columns.reduce(
            (acc: Record<string, RowColumnRender[]>, {data, render}, index) => {
                const element = acc[data];
                if (!element) {
                    acc[data] = [];
                }

                acc[data].push({
                    index: index,
                    render: render as (data: unknown) => number | string,
                });

                return acc;
            },
            {}
        );
        const options: Config = {
            destroy: true,
            language: getDatatableLanguagePack(i18n.language),
            paging: false,
            searching: false,
            order: [[1, "asc"]],
            ordering: true,
            lengthChange: false,
            info: false,
        }

        const invalidate = useCallback(() => {
            const table = dataTableRef.current?.dt();
            let invalidated = false;

            if (!table) return;
            if (rowsMap.current.size == 0) {
                table.clear();
            }

            snapshotsRef.current.forEach(snapshot => {
                const apiRow = rowsMap.current.get(snapshot.id);

                if (!apiRow) { // 새롭게 추가된 인스턴스
                    rowsMap.current.set(snapshot.id, table.row.add(snapshot));
                    invalidated = true;
                } else { // 이미 그려진 인스턴스
                    const rowElement = apiRow.node();
                    if (!rowElement) return;
                    const cells = rowElement.querySelectorAll('td, th');
                    const snapshotUnsafe = snapshot as unknown as Record<string, unknown>;
                    const tableDrawnSnapshotUnsafe = apiRow.data() as unknown as Record<string, unknown>;

                    // ui interaction이 없는 상태 필드만 아래 방식으로 변경.
                    ["name", "status"].forEach(
                        (key) => {
                            // if (tableDrawnSnapshotUnsafe[key] == snapshotUnsafe[key]) return;

                            tableDrawnSnapshotUnsafe[key] = snapshotUnsafe[key];

                            const columnRenders = columnRendersMap[key];
                            if (!columnRenders) return;
                            for (const columnRender of columnRenders) {
                                const render = columnRender.render;
                                const columnIndex = columnRender.index;

                                if (!render) {
                                    cells[columnIndex].innerHTML = (snapshotUnsafe[key] as object).toString();
                                } else {
                                    const meta = {
                                        settings: apiRow.context[0],
                                        row: apiRow.index(),
                                        col: columnIndex
                                    };
                                    cells[columnIndex].innerHTML = render(snapshotUnsafe[key], "display", "", meta).toString();
                                }
                            }
                        }
                    );
                }
            });

            const keys = [...rowsMap.current.keys()];
            for (const key of keys) {
                if (!snapshotsRef.current.some(instance => instance.id === key)) {
                    rowsMap.current.get(key)?.remove();
                    rowsMap.current.delete(key);
                    invalidated = true;
                }
            }

            if (invalidated) {
                table.draw();
                setRefreshTime(new Date());
            }
        }, [columnRendersMap]);

        // await snapshot.getSnapshotList();
        const refreshActionButtonLocale = useCallback(() => {
            // getDatatableLanguagePack(i18n.language),
            const datatableApi = dataTableRef.current?.dt();
            if (!datatableApi) return;

            datatableApi.$("[data-i18n-key]").each((_, e) => {
                e.innerHTML = t(e.dataset['i18nKey'] as string);
            });

            occurLangChange.current = true;
        }, [t]);


        useEffect(() => {
            i18n.on("languageChanged", refreshActionButtonLocale);

            return () => {
                i18n.off("languageChanged", refreshActionButtonLocale);
                if (!occurLangChange) {
                    checkedIdList.clear();
                    snapshotsRef.current = [];
                }
                occurLangChange.current = false;
            }
        }, [checkedIdList, i18n, refreshActionButtonLocale]);

        useEffect(() => {
            // 화면이 다시 그려질때 dom을 바인딩하여야 react가 정상 동작
            // option 의 createdRow를 사용하면 안됨
            const tableNodeApi = dataTableRef.current?.dt();
            if (!tableNodeApi) return;
            const rowsApi = tableNodeApi.rows;
            if (!rowsApi) return;
            const count = rowsApi().count();
            const data = tableNodeApi.data().toArray();

            const buttonSetEvents: {
                deleteButton: Element | null;
                deleteCallBack: () => void;

                modifyButton: Element | null;
                modifyCallBack: () => void;
            }[] = [];

            for (let i = 0; i < count; i++) {
                const headCell = tableNodeApi.cell(i, 0).node();
                if (headCell != undefined) {
                    const checkbox = headCell.querySelector("input[type=checkbox]") as HTMLInputElement;
                    const id = parseInt(checkbox.value);
                    checkbox.onchange = () => {
                        handleCheckboxChange(id, checkbox.checked);
                    }
                }
                const cell = tableNodeApi.cell(i, 5).node();
                if (cell != undefined) {

                    const deleteButton = cell.querySelector('.delete');
                    const modifyButton = cell.querySelector('.modify');
                    if (!deleteButton || !modifyButton) {
                        console.log("deleteButton or modifyButton is null");
                        continue;
                    }

                    const buttonsSet = {
                        deleteButton,
                        modifyButton,
                        deleteCallBack: () => props.onClickDelete(data[i]),
                        modifyCallBack: () => props.onSelectSnapshot(data[i]),
                    };
                    buttonSetEvents.push(buttonsSet);
                    deleteButton?.addEventListener?.('click', buttonsSet.deleteCallBack);
                    modifyButton?.addEventListener?.('click', buttonsSet.modifyCallBack);
                }
            }

            return () => {
                buttonSetEvents.forEach(({deleteButton, modifyButton, deleteCallBack, modifyCallBack}) => {
                    deleteButton?.removeEventListener("click", deleteCallBack);
                    modifyButton?.removeEventListener("click", modifyCallBack);
                });
            }

        }, [props, refreshTime]);

        return (
            <div style={{fontSize: "14px"}}>
                <DataTable
                    ref={dataTableRef}
                    data={snapshotsRef.current}
                    columns={columns}
                    className="display"
                    options={options}
                >
                    <thead>
                    <tr>
                        <th className="text-center">
                            <input
                                className="form-check-input" type={"checkbox"}
                                onClick={(e) => handleSelectAll(e)}
                            />
                        </th>
                        <th>{t("snapshot.snapshotName")}</th>
                        <th>{t("common.OS")}</th>
                        <th>{t("common.status")}</th>
                        <th>{t("common.creationTime")}</th>
                        <th>{t("common.action")}</th>
                    </tr>
                    </thead>
                    <tfoot>
                    <tr>
                        <td colSpan={columns.length} className="text-start text-secondary border-bottom">
                            {
                                isLoading
                                    ? <span>{t("common.loadingList")}</span>
                                    : <span>{snapshotsRef.current.length ?? 0} {t("snapshot.snapshotCount")}</span>
                            }
                        </td>
                    </tr>
                    </tfoot>
                </DataTable>
            </div>
        );
    }
);

export default SnapshotTable;